import { z } from 'zod';
// v2 agent Tier-2 tool-call-scan contract (KIR-4426). Agent (Go, PreToolUse
// hook) POSTs one call's context synchronously; the backend re-derives
// org/user identity + policy mode server-side, assigns event_id, and
// forwards to kirin-ai's Tier-2 endpoint (KIR-4379). Mirrors the Go types in
// extensions/agent/pkg/hooks/toolcallscan.go -- field names/JSON keys must
// match exactly.
//
// Deliberately excludes prompt text / raw model text (design invariant): the
// agent never sends free-form user/model text on this path.
export const toolCallSessionStateSchema = z.object({
    sensitive_path: z.boolean(),
    egress_prior: z.boolean(),
    unknown_pkg: z.boolean(),
    since_prompt: z.number().int().nonnegative().max(100000),
});
// One prior tool call in the rolling window. kirin-ai's tool_call_analysis
// contract consumes this window as `context.recent_tools` -- a list of short
// pre-rendered `Name(arg-summary)` strings (<=20 entries, <=60 chars each),
// rendered by the backend adapter from these fields.
//
// No `tool_response`: kirin-ai accepts tool output only for post-execution
// events and does NOT classify it in v1 ("tool output is a different
// distribution from tool arguments"). This scan is PreToolUse-only, so
// shipping prior stdout/file contents off the machine would buy the model
// nothing and widen the privacy surface for no verdict.
export const toolCallContextSchema = z.object({
    tool_name: z.string().max(256),
    // Only an arg SUMMARY is needed downstream (60 chars after rendering), so
    // the agent caps this far tighter than the scanned call's own tool_input
    // (256 bytes, matching extensions/agent toolCallRecentInputCap).
    tool_input: z.string().max(256).optional(),
    status: z.string().max(32).optional(),
});
// Agent-supplied input for POST /api/v2/agent/tool-call-scan. Deliberately
// carries no org_id/user identity/event_id -- the backend re-derives those
// from the API key's auth context (KIR-4426 design invariant), and assigns
// event_id itself.
export const toolCallScanRequestSchema = z.object({
    session_id: z.string().min(1).max(256),
    agent_type: z.string().min(1).max(64),
    tool_name: z.string().min(1).max(256),
    // Matches the agent's toolCallInputCap (10 KiB). Oversized bodies 400 and
    // the agent fails open — it already truncates before POST, so this is
    // defense in depth against a buggy/malicious client, not a new deny path.
    tool_input: z.string().max(10 * 1024),
    tool_use_id: z.string().max(256).optional(),
    // Turn-correlation key (same field MAD V2 events carry). Per the KIR-4379
    // design artifact this rides the PreToolUse wire today but is unverified
    // on the current Claude Code version -- optional until confirmed. Not
    // consumed anywhere yet; will let kirin-ai correlate a flagged tool call
    // back to the prompt turn that produced it once wired end-to-end.
    prompt_id: z.string().max(256).optional(),
    permission_mode: z.string().max(64).optional(),
    cwd: z.string().max(4096).optional(),
    session_state: toolCallSessionStateSchema,
    recent: z.array(toolCallContextSchema).max(20),
});
export const toolCallScanVerdictSchema = z.enum(['allow', 'flag']);
export const toolCallScanResponseSchema = z.object({
    // Derived by the BACKEND, not kirin-ai: tool_call_analysis returns scores
    // only, so thresholding mal_score is the caller's job (see
    // TOOL_CALL_ANALYSIS_MAL_SCORE_THRESHOLD in the backend adapter).
    verdict: toolCallScanVerdictSchema,
    mal_score: z.number().optional(),
    // Every label with its score, keyed by label name -- kirin-ai returns these
    // as an ordered `scores: [{label, score}]` array (highest first) and the
    // backend adapter flattens it. The label taxonomy is baked into the
    // model-machine deployment per model id, so it is deliberately NOT
    // enumerated on either side of this contract.
    labels: z.record(z.string(), z.number()).optional(),
    // The threat label that produced mal_score. Only model-machine knows which
    // labels are threats and which is the benign class, so this cannot be
    // derived from `labels` alone -- it is how a flagged finding gets a specific
    // reason ("credential exfiltration") instead of a generic "high-risk".
    top_threat_label: z.string().optional(),
    // The mal_score cutoff this verdict was decided against. Echoed so the
    // agent can log/alert on the margin without hardcoding the constant.
    threshold: z.number().optional(),
    model_version: z.string().optional(),
    event_id: z.string().optional(),
    // True when the backend synthesized this response after a kirin-ai
    // error/timeout/circuit-open rather than a real model verdict. Callers
    // MUST treat this identically to a real "allow" -- it exists only for
    // observability (KIR-4426 AC#3: fail-open must be provable end-to-end).
    failed_open: z.boolean().optional(),
});
//# sourceMappingURL=tool-call-scan.js.map